import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import { getApiErrorStatus } from '@/utils/apiError'
import type { UnregisteredCoverageCountResponse } from '../Types/Responses/CoverageResponses'
import {
  ImportableScheduleTreeCompany,
  ListAllSchedulesResponse,
  ListImportableSchedulesResponse,
  ListSchedulesResponse,
  PolicyVigencyStatusResponse,
  ScheduleConfigurationResponse,
  ScheduleDeletionPreviewResponse,
  ScheduleResponse
} from '../Types/Responses/ScheduleUnitResponses'
import {
  CreateSchedule,
  FiltersSchedule,
  ListImportableSchedulesParams,
  UpdateSchedule,
  CreateFullScheduleRequest,
  CreateFullScheduleResponse,
  UpdateVisualizationOrderPayload
} from '../Types/Requests/ScheduleRequests'
import { SelectOption } from '@/types'

export default class ScheduleService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/schedules'
  }

  async listAdminAccess(): Promise<SelectOption[]> {
    try {
      const { data } = await request.get<SelectOption[]>(`${this.getCompletePath()}/list-admin`)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as escalas que você administra.')
    }
  }

  async listAll(): Promise<ListAllSchedulesResponse[]> {
    try {
      const { data } = await request.get<ListAllSchedulesResponse[]>(
        `${this.getCompletePath()}/list`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as escalas.')
    }
  }

  async stationCreatingUsers(scheduleId: number): Promise<{ id: number; name: string }[]> {
    try {
      const { data } = await request.get<{ id: number; name: string }[]>(
        `${this.getCompletePath()}/${scheduleId}/station-creating-users`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os responsáveis pela escala.')
    }
  }

  async create(healthUnitId: number, payload: CreateSchedule): Promise<ScheduleResponse> {
    try {
      const { data } = await request.post<ScheduleResponse>(
        `${this.getCompletePath()}/healthUnit/${healthUnitId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível criar a escala.')
    }
  }

  async fullCreate(
    healthUnitId: number,
    payload: CreateFullScheduleRequest
  ): Promise<CreateFullScheduleResponse> {
    try {
      const { data } = await request.post<ScheduleResponse>(
        `${this.getCompletePath()}/healthUnit/${healthUnitId}/full-create`,
        payload
      )
      return data
    } catch (e) {
      // A extração manual de `response.data.message` (inclusive array) já é feita
      // por `handleError`/`getApiErrorMessage`, que também trata rede/timeout/5xx.
      throw this.handleError(e, 'Não foi possível criar a escala.')
    }
  }

  /**
   * Lista paginada das escalas que podem servir de origem no fluxo
   * "Importar configurações" da criação de escala.
   *
   * Pattern A (throw): a lista é o fluxo principal do modal — falha tem de chegar
   * ao usuário, não virar lista vazia silenciosa.
   */
  async listImportableConfigurations(
    params?: ListImportableSchedulesParams
  ): Promise<ListImportableSchedulesResponse> {
    try {
      // 2º argumento de request.get JÁ É o objeto de params — passar `{ params }`
      // aninharia duas vezes e a querystring sumiria em silêncio.
      const { data } = await request.get<ListImportableSchedulesResponse>(
        `${this.getCompletePath()}/importable-configurations`,
        params
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as escalas disponíveis para importação.')
    }
  }

  /**
   * Esqueleto Empresa → Unidade das escalas importáveis, sem as escalas.
   *
   * Uma chamada só: a estrutura da árvore não pagina, o custo é proporcional ao
   * número de unidades. As escalas vêm por unidade em `listImportableConfigurations`.
   */
  async listImportableTree(): Promise<ImportableScheduleTreeCompany[]> {
    try {
      const { data } = await request.get<ImportableScheduleTreeCompany[]>(
        `${this.getCompletePath()}/importable-configurations/tree`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as empresas e unidades disponíveis.')
    }
  }

  async list(filters?: FiltersSchedule): Promise<ListSchedulesResponse> {
    try {
      const { data } = await request.get<ListSchedulesResponse>(this.getCompletePath(), filters)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as escalas.')
    }
  }

  async remove(scheduleId: number): Promise<void> {
    try {
      await request.delete(`${this.getCompletePath()}/${scheduleId}`)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível excluir a escala.')
    }
  }

  /**
   * Q6 — resumo dos plantões futuros da escala antes de excluí-la. `null` =
   * backend sem a rota: o modal segue como antes (o DELETE continua protegido
   * no backend).
   */
  async getDeletionPreview(scheduleId: number): Promise<ScheduleDeletionPreviewResponse | null> {
    try {
      const { data } = await request.get<ScheduleDeletionPreviewResponse>(
        `${this.getCompletePath()}/${scheduleId}/deletion-preview`
      )
      return data
    } catch (e) {
      if ([404, 405, 501].includes(getApiErrorStatus(e) ?? 0)) return null
      throw this.handleError(e, 'Não foi possível carregar os plantões futuros da escala.')
    }
  }

  async getById(scheduleId: number): Promise<ScheduleResponse> {
    try {
      const response = await request.get<ScheduleResponse>(
        `${this.getCompletePath()}/${scheduleId}`
      )
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os dados da escala.')
    }
  }

  /**
   * Snapshot da configuração da escala, usado como origem no fluxo
   * "Importar configurações" da criação de escala.
   *
   * Pattern A (throw): sem o snapshot o import não tem o que preencher, então a
   * falha precisa chegar ao usuário.
   */
  async getImportConfiguration(scheduleId: number): Promise<ScheduleConfigurationResponse> {
    try {
      const { data } = await request.get<ScheduleConfigurationResponse>(
        `${this.getCompletePath()}/${scheduleId}/configuration`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as configurações da escala selecionada.')
    }
  }

  async update(
    scheduleId: number,
    payload: CreateSchedule | UpdateSchedule
  ): Promise<ScheduleResponse> {
    try {
      const { data } = await request.put<ScheduleResponse>(
        `${this.getCompletePath()}/${scheduleId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível salvar as alterações da escala.')
    }
  }

  /**
   * M8-B — quantos plantões ocupados sem identificação a escala tem (futuros e
   * passados). Usado só na confirmação de desligar a flag; `null` = backend sem
   * a rota, e o desligamento segue sem a contagem.
   */
  async getUnregisteredCoverageCount(
    scheduleId: number
  ): Promise<UnregisteredCoverageCountResponse | null> {
    try {
      const { data } = await request.get<UnregisteredCoverageCountResponse>(
        `${this.getCompletePath()}/${scheduleId}/unregistered-coverage-count`
      )
      return data
    } catch (e) {
      if ([404, 405, 501].includes(getApiErrorStatus(e) ?? 0)) return null
      throw this.handleError(e, 'Não foi possível contar os plantões ocupados sem identificação.')
    }
  }

  async getPolicyVigencyStatus(): Promise<PolicyVigencyStatusResponse> {
    try {
      const { data } = await request.get<PolicyVigencyStatusResponse>(
        `${this.getCompletePath()}/policy-vigency-status`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível verificar a vigência das políticas das escalas.')
    }
  }

  async updateVisualizationOrder(
    unitId: number,
    payload: {
      schedules: UpdateVisualizationOrderPayload
    }
  ): Promise<void> {
    try {
      await request.put(`${this.getCompletePath()}/healthUnit/${unitId}/ordering`, payload)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível salvar a ordenação das escalas.')
    }
  }
}
